/**
 * ReaderAuthContext — httpOnly-cookie edition
 *
 * Token is NEVER stored in localStorage.
 * On login/register/google-auth the backend sets a `tcl_rt` httpOnly cookie.
 * On mount we call /api/reader/me (credentials: 'include') to restore the
 * session from that cookie.  The raw token is kept in React state (memory
 * only) so existing code that passes Authorization: Bearer still works for
 * the lifetime of the current tab.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ReaderAuthContext = createContext(null);

// Reads the readable tcl_csrf cookie set by the backend.
const getCsrfToken = () => {
  const match = document.cookie.match(/(?:^|;\s*)tcl_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

// Fetch helper that always sends cookies and injects the CSRF token on
// state-changing requests (POST / PUT / PATCH / DELETE).
const api = (path, opts = {}) => {
  const method = (opts.method || 'GET').toUpperCase();
  const csrfHeaders = {};
  if (!SAFE_METHODS.has(method)) {
    const tok = getCsrfToken();
    if (tok) csrfHeaders['X-CSRF-Token'] = tok;
  }
  return fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...csrfHeaders, ...(opts.headers || {}) },
    ...opts,
  });
};

export const ReaderAuthProvider = ({ children }) => {
  const [user,         setUser]         = useState(null);
  const [token,        setToken]        = useState(null);   // in-memory only
  const [entitlements, setEntitlements] = useState([]);
  const [isLoading,    setIsLoading]    = useState(true);

  // ── session restore on mount (uses httpOnly cookie automatically) ──────────
  useEffect(() => {
    const restore = async () => {
      try {
        const res = await api('/api/reader/me');
        if (res.ok) {
          const userData = await res.json();
          setUser(userData);
          // Fetch entitlements while we're at it
          const entRes = await api('/api/reader/entitlements');
          if (entRes.ok) setEntitlements(await entRes.json());
          // token stays null — cookie handles auth from this point
        }
      } catch {
        // cookie absent or expired — silent
      } finally {
        setIsLoading(false);
      }
    };
    restore();
  }, []);

  // ── helpers ──────────────────────────────────────────────────────────────

  const _applyAuthResponse = (data) => {
    setToken(data.access_token);        // memory only, never localStorage
    setUser(data.user);
    setEntitlements(data.entitlements || []);
  };

  // ── public API ────────────────────────────────────────────────────────────

  const login = async (email, password) => {
    const res = await api('/api/reader/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Login failed');
    _applyAuthResponse(data);
    return data;
  };

  const register = async (email, password, firstName, lastName) => {
    const res = await api('/api/reader/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, first_name: firstName, last_name: lastName }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Registration failed');
    _applyAuthResponse(data);
    return data;
  };

  const loginWithGoogle = async (googleToken) => {
    const res = await api('/api/reader/google-auth', {
      method: 'POST',
      body: JSON.stringify({ google_token: googleToken }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Google auth failed');
    _applyAuthResponse(data);
    return data;
  };

  const logout = async () => {
    try {
      // Ask the backend to clear the httpOnly cookie
      await api('/api/reader/logout', { method: 'POST' });
    } catch { /* ignore network errors during logout */ }
    setToken(null);
    setUser(null);
    setEntitlements([]);
  };

  const activateEbook = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await api('/api/reader/activate-ebook', { method: 'POST', headers });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Activation failed');
    // Refresh entitlements
    const entRes = await api('/api/reader/entitlements', { headers });
    if (entRes.ok) setEntitlements(await entRes.json());
    return data;
  };

  const isAuthenticated = useCallback(() => !!(user), [user]);

  const hasEntitlement = useCallback((type) =>
    entitlements.some(e => e.entitlement_type === type && e.status === 'active'),
    [entitlements]
  );

  const hasEbookAccess  = useCallback(() => hasEntitlement('ebook_access'),  [hasEntitlement]);
  const hasCourseAccess = useCallback(() => hasEntitlement('course_access'), [hasEntitlement]);
  const hasPurchasedModule = useCallback((slug) =>
    entitlements.some(e => e.entitlement_type === slug && e.status === 'active'),
    [entitlements]
  );

  // Build an auth header for components that still do explicit fetch() calls
  const getAuthHeaders = useCallback(() => {
    if (token) return { Authorization: `Bearer ${token}` };
    return {};   // cookie will be sent automatically by the browser
  }, [token]);

  const refreshEntitlements = useCallback(async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await api('/api/reader/entitlements', { headers });
    if (res.ok) setEntitlements(await res.json());
  }, [token]);

  return (
    <ReaderAuthContext.Provider value={{
      user,
      token,
      entitlements,
      isLoading,
      login,
      register,
      loginWithGoogle,
      logout,
      activateEbook,
      isAuthenticated,
      hasEntitlement,
      hasEbookAccess,
      hasCourseAccess,
      hasPurchasedModule,
      getAuthHeaders,
      refreshEntitlements,
    }}>
      {children}
    </ReaderAuthContext.Provider>
  );
};

export const useReaderAuth = () => {
  const context = useContext(ReaderAuthContext);
  if (!context) {
    throw new Error('useReaderAuth must be used within a ReaderAuthProvider');
  }
  return context;
};
